'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import { IconLocationPin, IconRewindClock } from '@repo/svgs';
import Popover from '@/components/ui/Popover';
import ListCell, { ListCellSkeleton } from '@/components/ui/ListCell';
import useBuildingAutocomplete from '@/features/buildings/hooks/useBuildingAutocomplete';
import { SearchInputPill } from '@/components/ui/SearchInputPill';
import { useRouter } from 'next/navigation';
import { getSearchItemApi } from '@repo/api';
import {
  getRecentSearches,
  saveRecentSearch,
  type RecentBuildingSearch,
} from '@/utils/recentSearches';
import { buildingUrl } from '@/utils/routes';
import type { Building } from '@repo/api';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useStore } from '@/providers/StoreProvider';
import { Divider } from '@/components/ui/Divider';
import { BuildingSearchHistoryList } from './components/BuildingSearchHistoryList';

export interface SearchBarProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'onSubmit'> {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  onClear?: () => void;
  onBuildingSelect?: () => void;
}

export default function BuildingsSearchBar({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder = 'Search address or building',
  className,
  id,
  style,
  disabled,
  onBuildingSelect,
  ...rest
}: SearchBarProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isSelecting, setIsSelecting] = React.useState(false);
  const [recentSearches, setRecentSearches] = React.useState<RecentBuildingSearch[]>([]);
  const analytics = useAnalytics();
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const router = useRouter();

  const { suggestions, isLoading, query, setQuery, sessionId } = useBuildingAutocomplete(value, {
    minLength: 4, // length > 3
    debounceMs: 300,
  });

  const { onKeyDown: restOnKeyDown, onFocus: restOnFocus, onBlur: restOnBlur } = rest;

  React.useEffect(() => {
    if (disabled) return;
    if (isSelecting) {
      setOpen(false);
      return;
    }
    const trimmedLen = query.trim().length;
    const shouldShowAutocomplete = trimmedLen > 3;
    const shouldShowRecent = isFocused && !shouldShowAutocomplete && recentSearches.length > 0;

    // Open while focused so we can show either:
    // - recent searches (on focus, before autocomplete triggers), or
    // - loader/results/empty once query is long enough.
    setOpen(isFocused && (shouldShowAutocomplete || shouldShowRecent));
  }, [disabled, isFocused, query, recentSearches.length, suggestions.length, isLoading]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isSelecting) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        const submittedValue = (value ?? query ?? '').trim();
        onSubmit?.(submittedValue);
      }
      restOnKeyDown?.(e);
    },
    [isSelecting, onSubmit, value, query, restOnKeyDown],
  );

  const handleValueChange = React.useCallback(
    (next: string) => {
      if (isSelecting) return;
      setQuery(next);
      onChange?.(next);
    },
    [isSelecting, onChange, setQuery],
  );

  const handleClear = React.useCallback(() => {
    if (isSelecting) return;
    setQuery('');
    onClear?.();
  }, [isSelecting, onClear, setQuery]);

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Figma annotation: max 6 recent searches
      setRecentSearches(getRecentSearches().slice(0, 6));
      analytics.track('header_address_search', { type: 'home' });
      restOnFocus?.(e);
    },
    [analytics, restOnFocus],
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      restOnBlur?.(e);
      // Delay close so clicking within the popover can still fire (ListCell is not focusable).
      window.setTimeout(() => setIsFocused(false), 0);
    },
    [restOnBlur],
  );

  const openBuildingPage = async (placeId: string, address: string) => {
    try {
      // Immediately show the selected address in the input and a loader on the right.
      setIsSelecting(true);
      setOpen(false);
      setQuery(address);
      onChange?.(address);

      analytics.track('header_address_search_select', { type: 'home' });
      const building = await getSearchItemApi(placeId, sessionId, {
        credentials: isLoggedIn ? 'include' : 'omit',
      });

      // Save to recent searches
      saveRecentSearch(building);

      if (onBuildingSelect) {
        onBuildingSelect();
      }
      const href = buildingUrl({
        slug: building.buildingSlug,
        primaryAddress: { cityId: building.cityId, slug: building.slug },
        neighborhood: { city: building.cityId },
      } as unknown as Building);
      router.push(href);
    } catch {
      // best-effort: allow user to retry
      setIsSelecting(false);
    }
  };

  const openRecentBuilding = React.useCallback(
    (recent: RecentBuildingSearch) => {
      analytics.track('header_address_search_select', { type: 'home' });
      setOpen(false);
      setIsFocused(false);
      const address = (recent as any)?.address ?? (recent as any)?.buildingAddress ?? '';
      if (address) {
        setQuery(String(address));
        onChange?.(String(address));
      }
      // Bump this item to the top of recents
      saveRecentSearch(recent);
      if (onBuildingSelect) {
        onBuildingSelect();
      }

      const href = buildingUrl({
        slug: recent.buildingSlug,
        primaryAddress: { cityId: recent.cityId, slug: recent.slug },
        neighborhood: { city: recent.cityId },
      } as unknown as Building);
      router.push(href);
    },
    [analytics, onBuildingSelect, onChange, router, setQuery],
  );

  const trimmedLen = query.trim().length;
  const shouldShowAutocomplete = trimmedLen > 3;
  const shouldShowRecent = isFocused && !shouldShowAutocomplete && recentSearches.length > 0;

  return (
    <>
      <div
        ref={ref}
        role="search"
        aria-disabled={disabled || undefined}
        id={id}
        style={style}
        className={className}
      >
        <SearchInputPill
          id={'buildings-search-bar'}
          className="min-w-sm !shadow-lg !focus-within:shadow-lg "
          value={query}
          placeholder={placeholder}
          {...(disabled !== undefined && { disabled })}
          inputRef={inputRef}
          onValueChange={handleValueChange}
          {...(isSelecting ? {} : { onClear: handleClear })}
          isLoading={isSelecting}
          readOnly={isSelecting}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
      <Popover
        open={open}
        onOpenChange={o => (o ? setOpen(true) : setOpen(false))}
        anchorRef={ref as React.RefObject<HTMLElement>}
        matchAnchorWidth={true}
      >
        <div
          // Prevent input blur when clicking inside dropdown, so ListCell clicks always fire.
          onMouseDown={e => e.preventDefault()}
          className="rounded-4xl bg-primary border border-tertiary shadow-lg py-4 overflow-hidden"
        >
          {shouldShowAutocomplete ? (
            <div className="px-4">
              {isLoading ? (
                <div className="flex flex-col gap-1">
                  {[1, 2, 3].map(i => (
                    <React.Fragment key={i}>
                      <ListCellSkeleton className="bg-transparent rounded-xl hover:bg-secondary" />
                      {i !== 3 ? <Divider variant="tertiary" className="mx-2" /> : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : suggestions.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {suggestions.map((s, idx) => (
                    <React.Fragment key={s.placeId}>
                      <ListCell
                        title={s.address}
                        {...(s.borough ? { subtitle: s.borough } : {})}
                        leadingIcon={<IconLocationPin className="w-6 h-6 icon-secondary" />}
                        onClick={() => openBuildingPage(s.placeId, s.address)}
                        className="bg-transparent rounded-xl hover:bg-secondary"
                      />
                      {idx !== suggestions.length - 1 ? (
                        <Divider variant="tertiary" className="mx-2" />
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-2 text-center">
                  <p className="text-body-1-high text-primary">No buildings found</p>
                  <p className="text-body-2-normal text-secondary">
                    Try searching a building’s address (e.g., 200 Water St) or name (e.g., Silver
                    Towers).
                  </p>
                </div>
              )}
            </div>
          ) : shouldShowRecent ? (
            <div className="px-4">
              <BuildingSearchHistoryList
                recentSearches={recentSearches}
                onSelect={openRecentBuilding}
              />
            </div>
          ) : null}
        </div>
      </Popover>
    </>
  );
}
